import { createReducer, on } from '@ngrx/store';

import { isDeepEqual, isPresent } from '@fcom/core/utils/utils';

import * as InstantSearchActions from '../actions/instant-search.actions';
import { INSTANT_SEARCH_CACHE_EXPIRY } from '../constants';
import { InstantSearchResult, InstantSearchState } from '../store.interface';

export const initialState: InstantSearchState = {
  pricesForPeriod: [],
  fixedDeparture: [],
};

const isExpired = ({ createdAt }: InstantSearchResult<unknown>, currentTime: number): boolean =>
  isPresent(createdAt) && createdAt + INSTANT_SEARCH_CACHE_EXPIRY <= currentTime;

const prependResult = <P>(
  results: Array<InstantSearchResult<P>>,
  result: InstantSearchResult<P>
): Array<InstantSearchResult<P>> => [
  result,
  ...results.filter((other) => !isDeepEqual(other.params, result.params) && !isExpired(other, result.createdAt)),
];

const setPending = <P>(
  results: Array<InstantSearchResult<P>>,
  params: P,
  pending: boolean
): Array<InstantSearchResult<P>> =>
  results.map((result) => (isDeepEqual(result.params, params) ? { ...result, pending } : result));

const addPendingResult = <P>(results: Array<InstantSearchResult<P>>, params: P): Array<InstantSearchResult<P>> =>
  results.some((result) => isDeepEqual(result.params, params))
    ? setPending(results, params, true)
    : [{ params, pending: true }, ...results];

export const instantSearchReducer = createReducer(
  initialState,
  on(InstantSearchActions.pricesForPeriodRequestSent, (state, { params }) => ({
    ...state,
    pricesForPeriod: addPendingResult(state.pricesForPeriod, params),
  })),
  on(InstantSearchActions.pricesForPeriodResponseReceived, (state, { params, response, createdAt }) => ({
    ...state,
    pricesForPeriod: prependResult(state.pricesForPeriod, { params, response, createdAt }),
  })),
  on(InstantSearchActions.pricesForPeriodRequestFailed, (state, { params }) => ({
    ...state,
    pricesForPeriod: setPending(state.pricesForPeriod, params, false),
  })),
  on(InstantSearchActions.fixedDepartureRequestSent, (state, { params }) => ({
    ...state,
    fixedDeparture: addPendingResult(state.fixedDeparture, params),
  })),
  on(InstantSearchActions.fixedDepartureResponseReceived, (state, { params, response, createdAt }) => ({
    ...state,
    fixedDeparture: prependResult(state.fixedDeparture, { params, response, createdAt }),
  })),
  on(InstantSearchActions.fixedDepartureRequestFailed, (state, { params }) => ({
    ...state,
    fixedDeparture: setPending(state.fixedDeparture, params, false),
  }))
);
